// @ts-check
import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import React, { useEffect, useReducer, useState } from "react";

import NotificationAlertOptions from "@/layouts/Alerts";

import {
  getProducts,
  addingProductProperties,
  clearMessage,
} from "@/services/productPropertiesServices/ProductPropertiesActions";
import { getAllProperties } from "@/services/productServices/productActions";
import { getCategories } from "@/services/categoryServices/categoryAction";
import {
  findOverlayRow,
  getTaxonomyOverlays,
  overlayPathOf,
} from "@/services/taxonomyOverlayServices/taxonomyOverlayApi";

import AddProductPropertiesView from "./AddProductPropertiesView";
import { addProductPropertiesForm } from "./addProductPropertiesForm";
import type {
  CategoryOption,
  InitialPropertyRow,
  PropertiesProductRow,
  SubcategoryOption,
} from "./AddProductPropertiesView";
import type { TaxonomyOverlayRow } from "@/services/taxonomyOverlayServices/taxonomyOverlayApi";

/** A subcategory as GET /api/categories nests it under its category. */
type SubcategoryRow = {
  _id: string;
  name?: string;
  slug?: string;
  hostsProducts?: boolean;
  /** Per-country property ids: `{ key: country_code, value: propertyId[] }`. */
  properties?: { key?: string; value?: string[] }[];
};

type CategoryRow = {
  _id: string;
  name?: string;
  slug?: string;
  subcategories?: SubcategoryRow[];
};

const NO_OVERLAYS: TaxonomyOverlayRow[] = [];
const formActions = addProductPropertiesForm.actions;

const AddProductPropertiesContainer = () => {
  const dispatch = useAppDispatch();

  // SCRUM-771: per-slice selectors (object-literal selector re-rendered on every dispatch).
  const permission = useCurrentPermission();
  const allProperties = useAppSelector((s) => s.products.properties);
  const productsFromStore = useAppSelector((s) => s.productproperties.products);
  const message = useAppSelector((s) => s.productproperties.message);
  const categoriesFromStore = useAppSelector((s) => s.categories.categories);
  const currentSite = useAppSelector((s) => s.sites.site);

  // SCRUM-2220: the screen's draft (panel, chosen row, category, subcategory,
  // properties) is one createSlice mounted locally; the store's products and
  // categories are read and mapped during render.
  const [form, dispatchForm] = useReducer(
    addProductPropertiesForm.reducer,
    addProductPropertiesForm.getInitialState()
  );
  const {
    showPanel,
    selectedRow,
    categoryId,
    subcategoryId,
    showSubcategories,
    showProperties,
    selectedProperties,
  } = form;

  const products: PropertiesProductRow[] = productsFromStore || [];
  const categories: CategoryRow[] = categoriesFromStore || [];
  const mappedCategories: CategoryOption[] = categories.map((category) => ({
    value: { name: category.name, _id: category._id },
    label: category.slug,
  }));
  const categorySelected = categories.find((c) => c._id === categoryId);
  // A subcategory id is unique across categories, so it survives the category
  // select being cleared (the saved body keeps the chosen subcategory).
  const subcategorySelected = categories
    .flatMap((c) => c.subcategories || [])
    .find((sub) => sub._id === subcategoryId);
  // SCRUM-1718 (SCRUM-1413 D1): only a trunk — `hostsProducts: true` — hosts
  // product URLs, so a view node is never offered here; the server refuses
  // one anyway (422 SUBCATEGORY_NOT_TRUNK). An empty list is a real state the
  // view names, not an empty select.
  const mappedSubcategories: SubcategoryOption[] = (categorySelected?.subcategories || [])
    .filter((sub) => sub?.hostsProducts === true)
    .map((sub) => ({
      value: { name: sub.name, _id: sub._id, category: categorySelected!._id },
      label: sub.slug,
    }));

  useEffect(() => {
    dispatch(getProducts());
    dispatch(getCategories());
    dispatch(getAllProperties());
  }, [dispatch]);

  // SCRUM-1715 (SCRUM-1709 slice 2): the site's TaxonomyOverlay rows, read once
  // per site the way PageFormContainer reads them — the product page path takes
  // the category's overlay slug (the whole site-relative path since SCRUM-1673);
  // a category this site has not labelled through the overlay falls back to its
  // own slug (SCRUM-1851: no `label[]` step in between).
  const [siteOverlays, setSiteOverlays] = useState(NO_OVERLAYS);

  useEffect(() => {
    const siteId = currentSite?._id;
    if (!siteId) return undefined;
    let cancelled = false;
    getTaxonomyOverlays(siteId)
      .then((rows) => {
        if (!cancelled) setSiteOverlays(rows);
      })
      .catch(() => {
        // No rows is the fallback branch, not an error the form can act on.
        if (!cancelled) setSiteOverlays(NO_OVERLAYS);
      });
    return () => {
      cancelled = true;
    };
  }, [currentSite?._id]);


  // SCRUM-1263: no local errors-slice effect here. GlobalNotifications (SCRUM-857/883)
  // is the sole consumer of that slice and already toasts every shape the server sends;
  // measured, the two effects both ran, so an express-validator body produced TWO toasts
  // and two CLEAR_ERRORS, while the shapes this local guard did not recognise were
  // surfaced by the hub anyway. Drive screen-specific side-effects from this screen's own
  // signal, never by reading/clearing the shared slice.

  useEffect(() => {
    if (!message) return;
    NotificationAlertOptions("success", message);
    dispatch(clearMessage());
    dispatchForm(formActions.panelClosed());
  }, [message, dispatch]);


  const handleShowAdd = (_: unknown, row: PropertiesProductRow) => {
    dispatchForm(formActions.panelOpened(row));
  };

  const handleCategorySelect = (opt: CategoryOption | null) => {
    if (!opt) {
      dispatchForm(formActions.categoryCleared());
      return;
    }
    dispatchForm(
      formActions.categorySelected(categories.find((c) => c._id === opt.value._id)?._id ?? null)
    );
  };

  const handleSubcategorySelect = (opt: SubcategoryOption | null) => {
    if (!opt) {
      dispatchForm(formActions.subcategoryCleared());
      return;
    }

    const selectedCat = categories.find((c) => c._id === opt.value.category);
    const selSub = selectedCat?.subcategories?.find((s) => s._id === opt.value._id);

    // properties enabled for current site/country
    const subPropsIds =
      selSub?.properties?.find((p) => p.key === currentSite?.country_code)?.value ||
      [];

    const properties = (allProperties || []).filter((p: InitialPropertyRow) =>
      subPropsIds.includes(p._id)
    );

    dispatchForm(formActions.subcategorySelected({ subcategoryId: selSub?._id ?? null, properties }));
  };

  const handlePropertyChange = (name: string, value: string) => {
    dispatchForm(formActions.propertyChanged({ name, value }));
  };

  const handleAdd = () => {
    if (!selectedRow || !subcategorySelected) {
      NotificationAlertOptions("error", "Required Inputs");
      return;
    }

    // SCRUM-1715: the category's overlay slug for this site is the path, whole.
    // Without a usable row (no row, or a row without a slug — see
    // overlayPathOf) the category's own slug. SCRUM-1851 removed the step in
    // between — the stored `label[]` entry, gone from the schema with
    // SCRUM-1716 and stale beside the row.
    const overlayPath = overlayPathOf(
      findOverlayRow(siteOverlays, "category", categorySelected?._id)
    );
    const labelCategory = overlayPath ?? (categorySelected?.slug || "");

    const payload = {
      idProduct: selectedRow._id,
      idSubcategory: subcategorySelected._id,
      path:
        (labelCategory || "") +
        "/" +
        selectedRow.title.trim().replace(/\s+/g, "-").toLowerCase(),
      properties: selectedProperties,
    };

    dispatch(addingProductProperties(payload));
  };

  const canUse = !!(permission?.publish || permission?.edit);

  return (
    <AddProductPropertiesView
      // perms
      canUse={canUse}

      // data
      products={products}
      mappedCategories={mappedCategories}
      mappedSubcategories={mappedSubcategories}
      selectedRow={selectedRow}
      showPanel={showPanel}
      showSubcategories={showSubcategories}
      showProperties={showProperties}
      selectedProperties={selectedProperties}

      // handlers
      onShowAdd={handleShowAdd}
      onClosePanel={() => dispatchForm(formActions.panelClosed())}
      onCategoryChange={handleCategorySelect}
      onSubcategoryChange={handleSubcategorySelect}
      onPropertyChange={handlePropertyChange}
      onAdd={handleAdd}
    />
  );
};

export default AddProductPropertiesContainer;
